/**
 * VehiclePickerSection - Seleção de veículos nos modais financeiros
 *
 * Usado na criação de cobrança/assinatura para vincular 1..N veículos do
 * cliente ao documento financeiro (vínculo em nível de documento).
 *
 * Regras:
 *  - Só aparece quando o módulo de veículos está habilitado no tenant e um
 *    cliente já foi selecionado.
 *  - Lista apenas os veículos do próprio cliente (RPC list_customer_vehicles).
 *  - A gravação do vínculo acontece junto com a criação do documento
 *    (RPCs *_with_vehicles), garantindo atomicidade.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-18 v1.1.0 - Inclui veículos vinculados ao contato principal
 *          e mantém a seção visível com estado vazio após selecionar o cliente.
 * RELEASE: 2026-08-18 v1.0.0 - Criação inicial (Financeiro x Veículos)
 */

import { Car, Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useCustomerVehicles } from '@/hooks/useCustomerVehicles';
import { useVehiclesModuleEnabled } from '@/hooks/useVehiclesModule';

interface VehiclePickerSectionProps {
  /** Cliente dono dos veículos (null = seção oculta) */
  customerId: string | null;
  /** Contato principal do cliente, usado por vínculos legados/indiretos */
  contactId?: string | null;
  /** IDs selecionados */
  value: string[];
  /** Callback de alteração da seleção */
  onChange: (vehicleIds: string[]) => void;
  /** Habilita a busca (modal aberto) */
  enabled?: boolean;
  className?: string;
}

/** Rótulo curto do veículo (placa · marca modelo ano) */
function vehicleLabel(v: {
  plate: string | null;
  brand_name: string | null;
  model_name: string | null;
  year: number | null;
}): string {
  return [v.plate, [v.brand_name, v.model_name].filter(Boolean).join(' '), v.year]
    .filter(Boolean)
    .join(' · ');
}

export function VehiclePickerSection({
  customerId,
  contactId = null,
  value,
  onChange,
  enabled = true,
  className,
}: VehiclePickerSectionProps) {
  const vehiclesEnabled = useVehiclesModuleEnabled();
  const { vehicles, isLoading } = useCustomerVehicles(
    { customerId, contactId },
    vehiclesEnabled && enabled && !!(customerId || contactId),
  );

  // Sem módulo ou cliente selecionado, a seção não faz parte do fluxo atual.
  if (!vehiclesEnabled || !customerId) return null;

  /** Alterna a seleção de um veículo */
  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2 text-sm font-medium">
        <Car className="h-4 w-4 text-muted-foreground" />
        Veículos
        <span className="text-xs font-normal text-muted-foreground">(opcional)</span>
        {value.length > 0 && (
          <Badge variant="secondary" className="ml-1 h-5 text-[10px]">
            {value.length} selecionado{value.length > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : vehicles.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum veículo cadastrado para este cliente.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {vehicles.map((v) => {
            const selected = value.includes(v.id);
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => toggle(v.id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors',
                  selected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {selected ? <Check className="h-3.5 w-3.5" /> : <Car className="h-3.5 w-3.5" />}
                <span className="max-w-[240px] truncate">{vehicleLabel(v)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
